<script setup lang="ts">
import { computed, h, onMounted, ref, watch } from 'vue';
import { type RouteLocationRaw, useRouter } from 'vue-router';
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface';
import type { ContractManagementCalendarInDb } from '@/api';
import { CalendarService } from '@/services/calendarService';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useRowDelete } from '@/composables/useRowDelete';
import { useAuthStore } from '@/stores/auth';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import { gaiaContentWidth, gaiaNeutral } from '@/components/design/themes/tokens';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Calendars — the Calendar Events mock. A calendar is a prompt plus the
 * contract search it reads: Insights extracts dates from every contract in
 * scope and mails them
 * out on a schedule. The page is the same list anatomy as Views:
 * title + count, search, one primary action, and a data card whose rows carry
 * their own confirm/busy/error strips.
 *
 * Search, sort and paging stay server-side and URL-synced. Create and edit
 * left this page entirely — they are the full-page wizard now.
 */

const router = useRouter();
const message = useGaiaToast();
const authStore = useAuthStore();

const { currentPage, pageSize, searchQuery, sortKey, sortDirection, sortActivated } =
  useListQuerySync();

const calendars = ref<ContractManagementCalendarInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);
/**
 * Set when a refresh fails while rows from an earlier fetch are still on
 * screen: the rows stay and a banner says how old they are, because silently
 * showing stale rows as if they were current is the actual hazard.
 */
const staleSince = ref<string | null>(null);

// --- row state -------------------------------------------------------------
const rowDelete = useRowDelete<ContractManagementCalendarInDb>();

// --- derived ---------------------------------------------------------------
/**
 * Creating, updating and deleting a calendar are all org-admin endpoints. The
 * controls stay visible and say why rather than disappearing, so the capability
 * is discoverable to the person who has to go ask for it.
 */
const canManage = computed(() => authStore.userPermissions.is_org_admin);
const ADMIN_ONLY_HINT =
  'Only organisation admins can create, edit or delete calendars. Ask an admin in your workspace.';

const hasQuery = computed(() => searchQuery.value.trim().length > 0);

const countLabel = computed(() => {
  if (loading.value) return 'Loading';
  return `${totalCount.value} ${totalCount.value === 1 ? 'calendar' : 'calendars'}`;
});

const showEmpty = computed(
  () => !loading.value && !loadError.value && calendars.value.length === 0 && !hasQuery.value
);
const showNoMatch = computed(
  () => !loading.value && !loadError.value && calendars.value.length === 0 && hasQuery.value
);
const showRows = computed(() => !loading.value && calendars.value.length > 0);

const noMatchTitle = computed(() => `No calendars match “${searchQuery.value.trim()}”`);

// One entry per data column, in column order.
const skeletonCells = ['58%', '70%', '132px', '132px'] as const;

// The mock's columns, less its response type, which the API no longer has. `created_at` is not among them — it is the default
// sort, but no caret is drawn until the user picks one, so nothing on screen
// claims a choice they did not make.
const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'Name', width: 'minmax(180px,1.15fr)', sortable: true },
  { key: 'receiver_emails', label: 'Receiver emails', width: 'minmax(200px,1.4fr)' },
  { key: 'last_sent', label: 'Last sent', width: '190px' },
  { key: 'updated_at', label: 'Updated', width: '190px', sortable: true },
];

const rowMenuOptions = computed<DropdownMixedOption[]>(() => {
  const options: DropdownMixedOption[] = [
    { key: 'open', label: 'Open calendar' },
    { key: 'edit', label: 'Edit calendar', disabled: !canManage.value },
    { key: 'divider', type: 'divider' },
    {
      key: 'delete',
      label: 'Delete calendar',
      disabled: !canManage.value,
      props: canManage.value ? gaiaDangerOption : undefined,
    },
  ];
  // A dropdown has nowhere to hang a tooltip, so the reason the two greyed
  // items are greyed is a line in the menu itself.
  if (!canManage.value) {
    options.push({
      key: 'hint',
      type: 'render',
      render: () =>
        h(
          GaiaText,
          { size: 'meta', tone: 'faint', style: { display: 'block', padding: '6px 12px 4px' } },
          () => 'Organisation admins only.'
        ),
    });
  }
  return options;
});

// --- data ------------------------------------------------------------------
async function fetchCalendars(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    const result = await CalendarService.search({
      text: searchQuery.value || '',
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
      columnFilters: {},
    });
    calendars.value = result.results;
    totalCount.value = result.result_count;
    staleSince.value = null;
  } catch (error) {
    logger.error('Error fetching calendars:', error);
    if (calendars.value.length > 0) {
      staleSince.value = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      loadError.value = 'Couldn’t load calendars';
    }
  } finally {
    loading.value = false;
  }
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchCalendars();
}

watch(currentPage, () => {
  if (currentPage.value > 0) void fetchCalendars();
});

watch(pageSize, () => {
  if (pageSize.value > 0) void fetchCalendars();
});

onMounted(() => {
  void fetchCalendars();
});

// --- formatting ------------------------------------------------------------
const emptyCell = '—';

function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return emptyCell;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return emptyCell;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The first two addresses plus a `+N`; the rest live on the detail page. */
function visibleEmails(calendar: ContractManagementCalendarInDb): string[] {
  return calendar.receiver_emails.slice(0, 2);
}

function overflowEmails(calendar: ContractManagementCalendarInDb): number {
  return Math.max(0, calendar.receiver_emails.length - 2);
}

// --- toolbar ---------------------------------------------------------------
// Also the no-match state's "Clear search" button, which is why it stays a
// function rather than folding into the field's own clear.
function clearSearch(): void {
  searchQuery.value = '';
  currentPage.value = 1;
  void fetchCalendars();
}

function onSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    // Dates open newest-first; a name opens A–Z.
    sortDirection.value = key === 'name' ? 'asc' : 'desc';
  }
  currentPage.value = 1;
  void fetchCalendars();
}

// --- navigation ------------------------------------------------------------
function detailRoute(calendar: ContractManagementCalendarInDb): RouteLocationRaw {
  return {
    name: 'contract-management-calendar-detail',
    params: { uid: calendar.uid },
  };
}

function openCreate(): void {
  if (!canManage.value) return;
  void router.push({ name: 'contract-management-calendar-new' });
}

function openEdit(calendar: ContractManagementCalendarInDb): void {
  void router.push({
    name: 'contract-management-calendar-edit',
    params: { uid: calendar.uid },
  });
}

function onRowMenu(key: string, calendar: ContractManagementCalendarInDb): void {
  if (key === 'open') {
    void router.push(detailRoute(calendar));
    return;
  }
  if (key === 'edit') {
    openEdit(calendar);
    return;
  }
  rowDelete.arm(calendar);
}

async function deleteCalendar(calendar: ContractManagementCalendarInDb): Promise<void> {
  await rowDelete.run(calendar, {
    work: async () => {
      await CalendarService.delete(calendar.uid);
      message.success('Calendar deleted');
      await fetchCalendars();
    },
    errorText: () => 'Couldn’t delete this calendar. Nothing was changed.',
  });
}

// --- chrome ----------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.contractManagement }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.calendars }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="18">
      <!-- Title + count, toolbar pushed right. -->
      <n-flex align="flex-start" :size="[20, 12]">
        <n-flex vertical :size="6" :style="{ minWidth: 0 }">
          <n-flex align="baseline" :size="10" :style="{ minWidth: 0 }">
            <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
              {{ navLabels.calendars }}
            </gaia-header>
            <gaia-count-label>{{ countLabel }}</gaia-count-label>
          </n-flex>
          <gaia-text size="body" tone="tertiary" :style="{ maxWidth: '60ch' }">
            Scheduled digests of contract dates, sent to the people who need them.
          </gaia-text>
        </n-flex>

        <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchQuery"
            placeholder="Search calendars"
            :debounce="500"
            @search="applySearch"
          />

          <!-- The disabled button swallows pointer events, so the tooltip has
               to hang off a wrapper rather than the button itself. -->
          <gaia-tooltip :disabled="canManage" placement="bottom-end">
            <template #trigger>
              <span>
                <gaia-button
                  type="primary"
                  size="medium"
                  :disabled="!canManage"
                  @click="openCreate"
                >
                  <template #icon>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </template>
                  Create calendar
                </gaia-button>
              </span>
            </template>
            {{ ADMIN_ONLY_HINT }}
          </gaia-tooltip>
        </n-flex>
      </n-flex>

      <gaia-data-table
        :columns="tableColumns"
        :sort-key="sortKey"
        :sort-direction="sortDirection === 'asc' ? 'asc' : 'desc'"
        :sort-active="sortActivated"
        :action-width="56"
        @sort="onSort"
      >
        <template #banner>
          <gaia-banner v-if="staleSince" plain type="error">
            Couldn’t refresh — showing calendars as of {{ staleSince }}.
            <template #action>
              <gaia-button text size="tiny" @click="fetchCalendars">Retry</gaia-button>
            </template>
          </gaia-banner>
        </template>

        <template #default="{ rowStyle }">
          <!-- Loading: the toolbar and header stay live, the body shimmers. -->
          <gaia-table-skeleton v-if="loading" :row-style="rowStyle" :cells="skeletonCells" />

          <gaia-empty-state v-else-if="loadError" status="alert" :title="loadError">
            The server didn’t respond in time. Nothing was changed — retrying is safe.
            <template #actions>
              <gaia-button type="primary" size="small" @click="fetchCalendars">Retry</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showEmpty" title="No calendars yet">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="2.5" y="3.5" width="13" height="11" rx="1.5" />
                <path d="M2.5 7h13M6 2v3M12 2v3" />
              </svg>
            </template>
            Create a calendar to email upcoming renewal, expiry and obligation dates to the people
            responsible.
            <template #actions>
              <gaia-tooltip :disabled="canManage">
                <template #trigger>
                  <span>
                    <gaia-button
                      type="primary"
                      size="medium"
                      :disabled="!canManage"
                      @click="openCreate"
                    >
                      Create calendar
                    </gaia-button>
                  </span>
                </template>
                {{ ADMIN_ONLY_HINT }}
              </gaia-tooltip>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showNoMatch" :title="noMatchTitle">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            Search covers calendar names and receiver emails. Clear it to see every calendar.
            <template #actions>
              <gaia-button size="small" @click="clearSearch">Clear search</gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else-if="showRows">
            <template v-for="calendar in calendars" :key="calendar.uid">
              <!-- A row in trouble is replaced by its strip, never stacked with it. -->
              <gaia-table-row-state
                v-if="rowDelete.stateOf(calendar.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${calendar.name}”?`"
                @cancel="rowDelete.cancel()"
                @confirm="deleteCalendar(calendar)"
              >
                The digest stops for everyone subscribed to it. Extracted dates on the contracts are
                not deleted.
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(calendar.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ calendar.name }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(calendar.uid) === 'error'"
                variant="error"
                @retry="deleteCalendar(calendar)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(calendar.uid) }}
              </gaia-table-row-state>

              <div v-else class="row" :style="rowStyle" @click="router.push(detailRoute(calendar))">
                <div class="cell">
                  <gaia-text truncate size="body" weight="semibold" tone="ink">
                    <router-link
                      :to="detailRoute(calendar)"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ calendar.name }}
                    </router-link>
                  </gaia-text>
                </div>

                <div class="cell">
                  <template v-if="calendar.receiver_emails.length > 0">
                    <gaia-tag
                      v-for="email in visibleEmails(calendar)"
                      :key="email"
                      variant="muted"
                      size="tiny"
                    >
                      {{ email }}
                    </gaia-tag>
                    <gaia-tag v-if="overflowEmails(calendar) > 0" variant="muted" size="tiny">
                      +{{ overflowEmails(calendar) }}
                    </gaia-tag>
                  </template>
                  <gaia-text v-else size="body" tone="muted">{{ emptyCell }}</gaia-text>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    :tone="calendar.last_sent ? 'tertiary' : 'muted'"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDateTime(calendar.last_sent) }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDateTime(calendar.updated_at) }}
                  </gaia-text>
                </div>

                <n-flex justify="end" :size="0" :wrap="false">
                  <gaia-dropdown
                    :options="rowMenuOptions"
                    placement="bottom-end"
                    @select="(key: string) => onRowMenu(key, calendar)"
                  >
                    <gaia-icon-button :dimension="28" quaternary title="More" @click.stop>
                      ⋯
                    </gaia-icon-button>
                  </gaia-dropdown>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="showRows" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="(size: number) => (pageSize = size)"
          />
        </template>
      </gaia-data-table>
    </n-flex>
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  height: 56px;
  padding: 0 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
</style>
